<template>
    <div>
        <div class="input-wrapper inner-shadow active-inner">
            <div v-if="type == 'short-text'" class="input-area">
                <input v-model="localActiveValue" type="text" ref="textInputElement" @keyup.enter="handleEnterPressed" @input="handleStringChange">
            </div>
            <div v-if="type == 'number'" class="input-area">
                <input v-model="localActiveValue" type="text" ref="textInputElement" @keyup.enter="handleEnterPressed" @input="handleStringChange">
            </div>
            <div v-if="type == 'long-text'" class="input-area">
                <textarea v-model="localActiveValue" ref="textInputElement" :rows="rows" @input="handleStringChange"></textarea>
            </div>
            <div v-if="type == 'date'" class="input-area">
                <VueCtkDateTimePicker v-model="localActiveValue" @input="handleStringChange" :no-keyboard="true" :only-date="true" color="#28A0CF" position="top" :noLabel="true" :overlay="true" format="YYYY-MM-DD" ref="dateinput" />
            </div>
            <div v-if="type == 'datetime'" class="input-area">
                <VueCtkDateTimePicker v-model="localActiveValue" @input="handleStringChange" :no-keyboard="true" color="#28A0CF" position="top" :overlay="true" ref="dateinput" />
            </div>
            <div v-if="schedule" class="input-area">
                <VueCtkDateTimePicker v-model="activeScheduleDate" :no-keyboard="true" color="#28A0CF" position="top" :overlay="true" ref="dateinput" :min-date="minScheduleDate" :format="'YYYY-MM-DD HH:mm'" :no-button-now="true" :label="'Schedule date & time (UTC)'"/>
            </div>
            <div v-if="type == 'markdown'" class="input-area">
                <markdown-editor :value="localActiveValue" @input="handleEditorChange" :configs="easyMDEConfigs" :autoinit="false" ref="markdownEditor"></markdown-editor>
                <small>To preview the markdown content click on the eye icon in the editor.</small>
            </div>
            <div v-if="type == 'html'" class="input-area">
                <html-wrapper :value="localActiveValue" mode="active" @handleHtml="handleEditorChange" @handleWarning="handleHtmlWarning" ref="htmlEditor"></html-wrapper>
            </div>
            <div v-if="type == 'json'" class="input-area">
                <codemirror :value="localActiveValue" @input="handleEditorChange" :options="cmOptionsJS" ref="jsonEditor"></codemirror>
            </div>
            <single-select-list v-if="type == 'single-select'" :null-select="nullSelect" :options="options" :selected-value="localActiveValue" @selection-change="handleSingleSelectChange"></single-select-list>
            <multi-select-list v-if="type == 'multi-select'" :options="options" :selected-option-ids="localActiveValue" @selection-change="handleMultiSelectChange"></multi-select-list>
            <div class="input-action-area">
                <button :disabled="disableSaveButton || htmlEditorActive" type="button" class="btn btn-primary" v-if="!schedule" @click="handleSaveRequest(false)">Save</button>
                <button v-if="showSaveAndSync && !schedule" :disabled="disableSaveButton || htmlEditorActive" type="button" class="mr-2 btn btn-primary" @click="handleSaveRequest(true)">Save &amp; Sync</button>
                <button type="button" class="mr-2 btn btn-primary" v-if="showSchedule && !schedule" @click="handleSchedule"><i class="fa fa-clock"></i></button>
                <button type="button" class="mr-2 btn btn-primary" v-if="showSchedule && schedule" @click="scheduleContent" :disabled="activeScheduleDate === ''">Schedule</button>
                <button type="button" class="ml-1 btn btn-secondary" @click="handleCancel">Cancel</button>
            </div>
            <div v-if="unSaved || unSynced || htmlEditorActive" class="input-message-wrapper">
                <div class="input-message-area" :class="inputMessageStyle">
                    <div class="edit-in-place-icon">!</div>
                    {{ inputMessage }}
                </div>
            </div>
            <div v-if="showPreviewMasterValue" class="pt-2 pr-2 preview-master-value">
                <span class="master-value-label">Master Value:</span><br>
                <input v-if="isSingleLineDisplayType" v-model="masterValue" class="static-edit-in-place-value ghost-text">
                <textarea v-if="isMultiLineDisplayType" v-model="masterValue" rows="4" class="static-edit-in-place-value ghost-text"></textarea>
                <div v-if="isMultiSelectType">
                    <span v-for="masterVal in masterValue" :key="masterVal" class="pr-2 static-edit-in-place-value ghost-text">{{masterVal}}</span>
                </div>
            </div>
        </div>
    </div>
</template>
<style lang="scss">
.active-inner {
    .note-editable {
        white-space: initial !important;
    }
}
</style>
<script>
import markdownEditor from "vue-easymde/src/markdown-editor";

import VueCtkDateTimePicker from 'vue-ctk-date-time-picker';
import 'vue-ctk-date-time-picker/dist/vue-ctk-date-time-picker.css';
import moment from 'moment';

//json imports
import { codemirror } from 'vue-codemirror';
import 'codemirror/mode/javascript/javascript.js';
import 'codemirror/mode/markdown/markdown.js';
import 'codemirror/theme/base16-light.css';
import 'codemirror/addon/mode/overlay.js';
import 'codemirror/addon/edit/matchbrackets.js';
import 'codemirror/addon/edit/closebrackets.js';
import 'codemirror/addon/edit/matchtags.js';
import 'codemirror/addon/lint/lint.js';

import HTMLWrapper from './html-wrapper.vue';

import SingleSelectList from './single-select-list.vue';
import MultiSelectList from './multi-select-list.vue';

export default {
    name: 'active-mode',
    components: {
        'single-select-list': SingleSelectList,
        'multi-select-list': MultiSelectList,
        'markdown-editor': markdownEditor,
        "codemirror" : codemirror,
        'html-wrapper': HTMLWrapper,
        VueCtkDateTimePicker
    },
    props: {
        type: String,
        options: {
            type: Array,
            default() {
                return [];
            }
        },
        nullSelect: Boolean,
        activeValue: {
            type: [String, Array]
        },
        masterValue: {
            type: [String, Array]
        },
        showPreviewMasterValue: {
            type: Boolean,
            default: false
        },
        unSaved: {
            type: Boolean,
            default: false
        },
        unSynced: {
            type: Boolean,
            default: false
        },
        showSaveAndSync: {
            type: Boolean,
            default: false
        },
        rows: Number,
        showSchedule: {
            type: Boolean,
            default: false
        },
        minScheduleDate: {
            type: String,
            default() {
                const timezoneOffset = new Date().getTimezoneOffset() / 60;
                return moment().add(timezoneOffset, 'hour').add(10, 'minute').format('YYYY-MM-DD HH:mm');
            }
        }
    },
    data() {
        return {
            localActiveValue: this.activeValue,
            easyMDEConfigs : {
                autofocus: true,
                hideIcons: ['fullscreen', 'side-by-side'],
                autoDownloadFontAwesome: false,
                togglePreview: true
            },
            cmOptionsJS: {
                tabSize: 4,
                mode: 'application/json',
                theme: 'default',
                lineNumbers: true,
                line: true,
                matchBrackets: true,
                matchTags: true,
                autoCloseBrackets: true,
                autoCloseTags: true,
                extraKeys: {"Ctrl-Space":"autocomplete"},
                lint: true,
                gutters: ["CodeMirror-lint-markers"],
            },
            activeScheduleDate: this.minScheduleDate,
            htmlEditorActive: false,
            schedule: false
        };
    },
    watch: {
        activeValue(newValue) {
            this.localActiveValue = newValue;
        },
    },
    computed: {
        isSingleLineDisplayType() {
            return (this.type === 'short-text' || this.type === 'number' || this.type === 'datetime' || this.type === 'date' || this.type === 'single-select');
        },
        isMultiLineDisplayType() {
            return (this.type === 'long-text' || this.type === 'markdown' || this.type === 'json' || this.type === 'html');
        },
        isMultiSelectType() {
            return this.type === 'multi-select';
        },
        inputMessageStyle() {
            // calculate css styles depending on the current state of the component
            let styles={};

            if (this.htmlEditorActive) {
                styles['input-message-editor-active'] = true;
            }

            if (this.unSaved) {
                styles['input-message-not-saved'] = true;
            }

            if (this.unSynced) {
                styles['input-message-not-synced'] = true;
            }

            return styles;

        },
        inputMessage() {
            if (this.inputError !== '') {
                return this.inputError;
            }
            if (this.unSynced) {
                return "Unsynced Changes";
            }
            if (this.unSaved) {
                return "Unsaved Changes";
            }
            if (this.htmlEditorActive) {
                return "Disable codeview to save"
            }
        },
        inputError() {
            if (this.type == 'number') {
                if (isNaN(this.localActiveValue)) {
                    return 'Numbers Only'
                }
            }
            return '';
        },
        disableSaveButton() {
            return (this.inputError !== '');
        }
    },
    methods: {
        handleSchedule() {
            this.schedule = !this.schedule;
        },
        scheduleContent() {
            this.$emit('schedule', this.activeScheduleDate);
        },
        handleEditorChange(value) {
            this.localActiveValue = value;

            this.$emit('value-change', this.localActiveValue);
        },
        handleEnterPressed() {
            this.$emit('entered-pressed');
        },
        handleStringChange() {
            this.$emit('value-change', this.localActiveValue);
        },
        handleMultiSelectChange(optionId) {
            // tell listeners that the user has changed value
            this.$emit('value-change', optionId);
        },
        handleSingleSelectChange(option) {
            // tell listeners that the user has changed value
            this.$emit('value-change', option.label);
        },
        handleSaveRequest(withSync) {
            // tell listeners that the user wants to save this value
            this.$emit('save', withSync);
        },
        handleCancel() {
            this.schedule = false;
            this.$emit('cancelled');
        },
        handleHtmlWarning(editorActive) {
            this.htmlEditorActive = editorActive;
        }
    }
}
</script>
<style>
</style>
